import React from 'react'
import { Dialog } from '@mui/material'
import { Close as CloseIcon, ContentCopy as CopyIcon, EditOutlined } from '@mui/icons-material'
import { Package, Workflow } from '../../store/package/reducer'
import {
  categoryForItem, fallbackReturns, fieldHint, HIDDEN_FROM_SEARCH_TYPES, InputKind, isApiOnlyType,
  SOURCE_EXPAND_TYPES, sourceInputKind, sourceLabel, typeMeta, typeName,
} from './taxonomy'
import { AuditLog } from './AuditLog'
import l from '../../page/SubscribersPageV2.module.css'
import s from './WorkflowPreviewDialog.module.css'

/*
 * "How the user sees this workflow" — the workflow as the user's Search page
 * draws it (espy-dashboard searcher-v2: WorkflowCards — step 2 "Search
 * method", WhatYouGetRail — "You will get", and the step 3 search field).
 * Ported, not shared. The enrichment the dashboard adds — description,
 * returned fields, typical runtime, per-source cards, API-only lock — comes
 * from the same taxonomy (./taxonomy.ts, a copy), so the picture matches.
 *
 * Step 2 shows every workflow of this type, as the user gets them, with this
 * one selected; another card can be clicked to compare. Prices follow the
 * chosen package: credits (price × its credits per unit) when the package is
 * credit-based, euros otherwise — what that package's user sees.
 */

export type Item = Workflow & {
  description: string
  returns: string[]
  eta?: string
  apiOnly: boolean
  kind: InputKind
}
export type Option = { key: string; item: Item; source?: string; label: string; kind: InputKind }

/* the dashboard's catalogue item (useLookupCatalog) for one workflow row */
export function toItem(w: Workflow): Item {
  const meta = typeMeta(String(w.type), w.title)
  const subtypes: string[] = Array.isArray(w.sources) ? (w.sources as any[]).map(String) : []
  const category = categoryForItem(subtypes, meta.category)
  const any = w as any
  return {
    ...w,
    description: any.description || w.subTitle || meta.description || '',
    returns: (any.returns as string[]) || meta.returns || fallbackReturns(category),
    eta: any.eta || meta.eta,
    apiOnly: isApiOnlyType(String(w.type)),
    kind: meta.inputKind,
  }
}

/* step 2's cards for a type — one per row, or one per source for SOURCE_EXPAND_TYPES */
export function optionsFor(rows: Item[], type: string): Option[] {
  if (!SOURCE_EXPAND_TYPES.includes(type)) {
    return rows.map((item) => ({ key: String(item.id), item, label: item.title, kind: item.kind }))
  }
  const seen = new Set<string>()
  const out: Option[] = []
  rows.forEach((item) => {
    const sources = Array.isArray(item.sources) ? (item.sources as any[]).map(String) : []
    if (!sources.length) {
      out.push({ key: String(item.id), item, label: item.title, kind: item.kind })
      return
    }
    sources.forEach((src) => {
      if (seen.has(src)) return
      seen.add(src)
      out.push({ key: `${item.id}-${src}`, item, source: src, label: sourceLabel(src), kind: sourceInputKind(src, item.kind) })
    })
  })
  return out
}

const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m5 13 4 4L19 7" /></svg>
)

/* step 2 — one "Search method" card (WorkflowCards) */
export function MethodCard({ option: o, active, isThis, price, onClick }: {
  option: Option; active: boolean; isThis?: boolean; price: string; onClick?: () => void
}) {
  return (
    <button
      type="button"
      className={`${s.card} ${active ? s.cardOn : ''} ${o.item.apiOnly ? s.cardApi : ''} ${isThis ? s.cardThis : ''}`}
      onClick={onClick}
      title={o.item.apiOnly ? 'Available through the Developer API only — not runnable here' : undefined}
    >
      {o.item.apiOnly && <span className={s.apiRibbon}>API only</span>}
      <span className={s.radio}>{active && <span />}</span>
      <span className={s.cardText}>
        <b>{o.label}</b>
        <span>{o.source ? o.item.title : (o.item.description || ' ')}</span>
      </span>
      <span className={s.price}>{price}</span>
    </button>
  )
}

/* step 3 — the search field with its hint and button */
export function SearchField({ placeholder, button }: { placeholder: string; button?: string }) {
  return (
    <div className={s.search}>
      <div className={s.input}>{placeholder || 'Search value'}</div>
      <span className={s.go}>{button || 'Search'}</span>
    </div>
  )
}

/* "You will get" (WhatYouGetRail) */
export function YouWillGet({ item, price }: { item: Item; price: string }) {
  return (
    <aside className={s.rail}>
      <div className={s.railTitle}>You will get</div>
      <b className={s.railName}>{item.title}</b>
      {item.description && <p className={s.railDesc}>{item.description}</p>}
      {item.returns.length > 0 && (
        <ul className={s.returns}>
          {item.returns.map((r) => <li key={r}><span><CheckIcon /></span>{r}</li>)}
        </ul>
      )}
      <div className={s.stats}>
        <div><small>Cost</small><b>{price}</b></div>
        {item.eta && <div><small>Typical</small><b>{item.eta}</b></div>}
      </div>
      <p className={s.note}>Fields vary by source coverage.</p>
    </aside>
  )
}

export function WorkflowPreviewDialog({ workflow, workflows, packages, theme, onClose, onEdit, onDuplicate }: {
  workflow: Workflow
  /** every workflow — step 2 shows the ones of the same type */
  workflows: Workflow[]
  packages: Package[]
  theme: 'light' | 'dark'
  onClose: () => void
  onEdit: (w: Workflow) => void
  onDuplicate?: (w: Workflow) => void
}) {
  const type = String(workflow.type)
  const options = React.useMemo(() => {
    const rows = workflows.filter((w) => String(w.type) === type).map(toItem)
    // the console puts API-only cards last
    return optionsFor(rows, type).sort((a, b) => Number(a.item.apiOnly) - Number(b.item.apiOnly))
  }, [workflows, type])
  const [selected, setSelected] = React.useState(() => options.find((o) => o.item.id === workflow.id)?.key || options[0]?.key)
  const current = options.find((o) => o.key === selected) || options[0]

  // prices as the chosen package's user sees them — a credit-based package first
  const pricedPackages = React.useMemo(
    () => [...packages].sort((a, b) => Number(!!b.creditsPerUnit) - Number(!!a.creditsPerUnit) || Number(!!b.isActive) - Number(!!a.isActive)),
    [packages],
  )
  const [pkgId, setPkgId] = React.useState<number | ''>(() => pricedPackages.find((p) => p.creditsPerUnit && p.isActive)?.id ?? '')
  const pkg = pricedPackages.find((p) => p.id === pkgId)
  const creditsPerUnit = pkg?.creditsPerUnit || 0
  const price = (w: Workflow) => (creditsPerUnit ? `${Math.round(Number(w.price) * creditsPerUnit)} cr` : `€${w.price}`)

  const hidden = HIDDEN_FROM_SEARCH_TYPES.includes(type)
  // the Search page view, or what admins changed in this workflow (admin_audit)
  const [view, setView] = React.useState<'preview' | 'history'>('preview')
  // the workflow whose card is selected — Edit and Duplicate act on it
  const shownWorkflow = current?.item.id === workflow.id ? workflow : (workflows.find((w) => w.id === current?.item.id) || workflow)
  const placeholder = current ? fieldHint(current.kind, current.source, (current.item as any).input) : ''

  return (
    <Dialog open onClose={onClose} maxWidth={false} PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', m: 2 } }}>
      <div className={`${l.theme} ${s.shell}`} data-theme={theme}>
        <div className={s.top}>
          <div className={s.topText}>
            <small>Preview</small>
            <b>How the user sees “{workflow.title}”</b>
          </div>
          <label className={s.pkg}>
            Prices for
            <select value={pkgId} onChange={(e) => setPkgId(e.target.value === '' ? '' : Number(e.target.value))}>
              <option value="">Workflow price (€)</option>
              {pricedPackages.map((p) => (
                <option key={p.id} value={p.id}>{p.title}{p.creditsPerUnit ? ` · ${p.creditsPerUnit} cr/€` : ' · €'}{p.isActive ? '' : ' (inactive)'}</option>
              ))}
            </select>
          </label>
          <button type="button" className={s.close} onClick={onClose} aria-label="Close"><CloseIcon sx={{ fontSize: 18 }} /></button>
        </div>

        {hidden && <div className={s.banner}>This type is hidden from the Search page — users do not see it there.</div>}

        <div className={s.viewTabs}>
          <button type="button" className={`${s.viewTab} ${view === 'preview' ? s.viewOn : ''}`} onClick={() => setView('preview')}>Preview</button>
          <button type="button" className={`${s.viewTab} ${view === 'history' ? s.viewOn : ''}`} onClick={() => setView('history')}>History</button>
        </div>

        {view === 'history' ? (
          <div className={s.history}><AuditLog entityType="lookup" entityId={shownWorkflow.id} compact /></div>
        ) : (
        <div className={s.body}>
          <div className={s.left}>
            <div className={s.stepHead}><span>2</span>Search method · <em>{typeName(type)}</em></div>
            <div className={s.cards}>
              {options.map((o) => {
                return (
                  <MethodCard key={o.key} option={o} active={o.key === current?.key} isThis={o.item.id === workflow.id} price={price(o.item)} onClick={() => setSelected(o.key)} />
                )
              })}
            </div>
            {options.length > 1 && <div className={s.legend}><i />this workflow · the others share its type on the user&apos;s screen</div>}

            <div className={s.stepHead}><span>3</span>Search value</div>
            <SearchField placeholder={placeholder} button={(current?.item as any)?.button || workflow.button} />
          </div>

          {current && <YouWillGet item={current.item} price={price(current.item)} />}
        </div>
        )}

        <div className={s.foot}>
          <span className={s.footNote}>{pkg ? `${pkg.title}: ${creditsPerUnit ? `${creditsPerUnit} credits per €` : 'priced in euros'}` : 'The workflow’s own price'}</span>
          {onDuplicate && (
            <button type="button" className={s.dup} onClick={() => onDuplicate(shownWorkflow)}>
              <CopyIcon sx={{ fontSize: 16 }} />Duplicate
            </button>
          )}
          <button type="button" className={`${s.edit} ${onDuplicate ? s.editNext : ''}`} onClick={() => onEdit(shownWorkflow)}>
            <EditOutlined sx={{ fontSize: 17 }} />Edit {current && current.item.id !== workflow.id ? `“${current.item.title}”` : 'workflow'}
          </button>
        </div>
      </div>
    </Dialog>
  )
}
